import { useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Phone, Mail, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { updateMetaTag } from "@/lib/seo-utils";
import { updateCanonicalTag, getProductionUrl } from "@/lib/canonical-utils";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { motion } from "framer-motion";

const ThankYou = () => {
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref") || "quote";
  
  useEffect(() => {
    // Hardcoded SEO for Thank You page
    document.title = "Thank You - We'll Be In Touch Soon | ShoreSight Marine Windows";
    
    const metaDescription = "Thank you for contacting ShoreSight Marine Windows. We've received your inquiry and will respond within 24 hours with a detailed quote for your marine window needs.";
    
    // Meta Description
    updateMetaTag("name", "description", metaDescription);
    
    // Open Graph
    updateMetaTag("property", "og:title", document.title);
    updateMetaTag("property", "og:description", metaDescription);
    updateMetaTag("property", "og:type", "website");
    
    // Twitter Card
    updateMetaTag("name", "twitter:card", "summary");
    updateMetaTag("name", "twitter:title", document.title);
    updateMetaTag("name", "twitter:description", metaDescription);
    
    // Robots - Don't index thank you pages
    updateMetaTag("name", "robots", "noindex, nofollow");

    // Canonical and OG URL
    const canonicalUrl = getProductionUrl("/thank-you");
    updateCanonicalTag(canonicalUrl);
    updateMetaTag("property", "og:url", canonicalUrl);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="min-h-screen bg-navy-900">
      <Header />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 md:py-20">
        <motion.div 
          className="max-w-4xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Success Icon & Message */}
          <motion.div className="text-center mb-12" variants={itemVariants}>
            <motion.div 
              className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-azure-500/20 mb-6 ring-4 ring-azure-500/10"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
            >
              <CheckCircle2 className="w-12 h-12 text-azure-500" />
            </motion.div>
            <h1 className="text-3xl md:text-5xl font-serif font-bold text-white mb-4">
              Thank You for Your Inquiry!
            </h1>
            <p className="text-lg text-seafoam-300">
              We've received your {ref === "quote" ? "quote request" : "message"} and will review it carefully.
            </p>
          </motion.div>

          {/* What Happens Next */}
          <motion.div variants={itemVariants}>
            <Card className="mb-8 bg-navy-800/50 backdrop-blur-sm border-azure-500/20 shadow-xl shadow-black/20">
              <CardContent className="pt-6">
                <h2 className="text-2xl font-semibold text-white mb-6">
                  What Happens Next?
                </h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-azure-500/20 flex items-center justify-center text-azure-500 font-semibold text-lg">
                      1
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Review</h3>
                      <p className="text-sm text-gray-300">
                        Our team will carefully review your vessel details and service requirements.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-azure-500/20 flex items-center justify-center text-azure-500 font-semibold text-lg">
                      2
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Response</h3>
                      <p className="text-sm text-gray-300">
                        We'll contact you within 24 hours (usually much sooner) to discuss your project.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-azure-500/20 flex items-center justify-center text-azure-500 font-semibold text-lg">
                      3
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Quote</h3>
                      <p className="text-sm text-gray-300">
                        If needed, we'll schedule a time to inspect your vessel and provide a detailed quote.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Options */}
          <motion.div variants={itemVariants}>
            <Card className="mb-8 bg-navy-800/50 backdrop-blur-sm border-azure-500/20 shadow-xl shadow-black/20">
              <CardContent className="pt-6">
                <h2 className="text-2xl font-semibold text-white mb-6">
                  Need Immediate Assistance?
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <a
                    href="tel:+16193774673"
                    className="flex items-center gap-3 p-4 rounded-lg border border-azure-500/20 bg-white/5 hover:bg-white/10 hover:border-azure-500 transition-all duration-300 touch-target"
                  >
                    <Phone className="w-5 h-5 text-azure-500" />
                    <div>
                      <div className="font-medium text-white">Call Us</div>
                      <div className="text-sm text-seafoam-300">(619) 377-4673</div>
                    </div>
                  </a>
                  <a
                    href="mailto:info@shoresightmarine.com"
                    className="flex items-center gap-3 p-4 rounded-lg border border-azure-500/20 bg-white/5 hover:bg-white/10 hover:border-azure-500 transition-all duration-300 touch-target"
                  >
                    <Mail className="w-5 h-5 text-azure-500" />
                    <div>
                      <div className="font-medium text-white">Email Us</div>
                      <div className="text-sm text-seafoam-300">info@shoresightmarine.com</div>
                    </div>
                  </a>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* CTA */}
          <motion.div className="text-center" variants={itemVariants}>
            <Link to="/">
              <Button 
                size="lg" 
                className="gap-2 bg-white hover:bg-white/90 text-navy-900 font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 px-8 py-6 text-lg"
              >
                <Home className="w-5 h-5" />
                Return to Homepage
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default ThankYou;
